import React, { useState, useEffect, useRef } from 'react';
import { useSocket } from '../context/SocketContext';
import { Send, MessageCircle, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../utils/api';
import { format } from 'date-fns';

const ChatBox = ({ role }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const socket = useSocket();

  useEffect(() => {
    fetchMessages();
  }, []);

  useEffect(() => {
    if (socket) {
      socket.on('new_message', (message) => {
        setMessages(prev => {
          // Avoid duplicate: if we already have this message (optimistically added),
          // replace the temp entry by matching on id; otherwise just append.
          const exists = message.id && prev.some(m => m.id === message.id);
          if (exists) {
            return prev.map(m => (m.id === message.id ? message : m));
          }
          return [...prev, message];
        });
        scrollToBottom();
      });

      return () => {
        socket.off('new_message');
      };
    }
  }, [socket]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const response = await api.getMessages();
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const messageText = newMessage.trim();

    // ── OPTIMISTIC UPDATE ──────────────────────────────────────────────────
    // Show the message in the UI immediately so the sender doesn't have to
    // wait for the server round-trip + socket echo before seeing their own
    // message. We give it a temporary id so the socket handler can detect
    // and skip the duplicate when the real event arrives.
    const tempId = `temp_${Date.now()}`;
    const optimisticMsg = {
      id: tempId,
      sender_role: role,
      message: messageText,
      timestamp: new Date().toISOString(),
      _optimistic: true,
    };
    setMessages(prev => [...prev, optimisticMsg]);
    setNewMessage('');
    // ──────────────────────────────────────────────────────────────────────

    try {
      await api.sendMessage(messageText);
      // The socket 'new_message' event from the server will arrive and the
      // handler above replaces or deduplicates the optimistic entry.
    } catch (error) {
      // Roll back the optimistic message on failure
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setNewMessage(messageText);
      toast.error('Failed to send message');
    }
  };

  const otherRole = role === 'admin' ? 'chef' : 'admin';

  return (
    <div className="relative">
      {/* Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 lg:static lg:bottom-auto lg:right-auto bg-[#E65100] text-white p-4 rounded-full shadow-lg hover:bg-[#D84800] transition flex items-center gap-2"
          data-testid="open-chat-button"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="hidden lg:inline font-medium">Chat with {otherRole}</span>
        </button>
      )}

      {/* Chat Box */}
      {isOpen && (
        <div className="fixed bottom-0 right-0 lg:static w-full lg:w-auto bg-white border-t lg:border lg:rounded-xl shadow-2xl lg:shadow-none z-50">
          <div className="flex flex-col h-[500px] lg:h-[400px] lg:w-[400px]">
            {/* Header */}
            <div className="bg-[#E65100] text-white p-4 flex items-center justify-between rounded-t-xl">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                <h3 className="font-semibold">Chat with {otherRole}</h3>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-white/20 rounded"
                data-testid="close-chat-button"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
              {messages.map((msg, idx) => (
                <div
                  key={msg.id || idx}
                  className={`flex ${msg.sender_role === role ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[75%] rounded-lg p-3 ${
                      msg.sender_role === role
                        ? 'bg-[#E65100] text-white'
                        : 'bg-white border border-gray-200 text-gray-900'
                    } ${msg._optimistic ? 'opacity-80' : ''}`}
                  >
                    <p className="text-sm">{msg.message}</p>
                    <p className={`text-xs mt-1 ${
                      msg.sender_role === role ? 'text-white/70' : 'text-gray-500'
                    }`}>
                      {format(new Date(msg.timestamp), 'HH:mm')}
                      {msg._optimistic && (
                        <span className="ml-1 italic">sending…</span>
                      )}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-4 border-t border-gray-200 bg-white">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1"
                  data-testid="chat-message-input"
                />
                <Button
                  type="submit"
                  disabled={!newMessage.trim()}
                  className="bg-[#E65100] hover:bg-[#D84800] text-white"
                  data-testid="send-message-button"
                >
                  <Send className="w-5 h-5" />
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatBox;
